import { ChevronDown, ChevronUp, Trash2, Wind, FileText } from "lucide-react";
import { BREATHING_PRESETS, videoEmbedUrl } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { AiWriteButton } from "@/components/AiWriteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const presetFor = (b) => Object.entries(BREATHING_PRESETS).find(([k, p]) => k !== "custom" && p.inhale === b.inhale && p.hold_in === b.hold_in && p.exhale === b.exhale && p.hold_out === b.hold_out)?.[0] || "custom";

const BreathingFields = ({ step, update, index, tutorialTitle }) => {
  const b = step.breathing || { inhale: 4, hold_in: 4, exhale: 4, hold_out: 4, intro: "" };
  const preset = presetFor(b);
  const setB = (patch) => update({ breathing: { ...b, ...patch } });
  return (
    <div className="rounded-2xl bg-indigo-50/60 border border-indigo-100 p-4 space-y-4">
      <div className="space-y-2">
        <Label>Breathing pattern</Label>
        <Select value={preset} onValueChange={(k) => { const { name, ...vals } = BREATHING_PRESETS[k]; setB(k === "custom" ? { pattern: "custom" } : { pattern: k, ...vals }); }}>
          <SelectTrigger className="bg-white rounded-xl" data-testid={`step-${index}-pattern-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(BREATHING_PRESETS).map(([k, p]) => <SelectItem key={k} value={k}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["inhale", "Inhale"], ["hold_in", "Hold"], ["exhale", "Exhale"], ["hold_out", "Hold"]].map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{label} (s)</Label>
            <Input type="number" min={k.startsWith("hold") ? 0 : 1} max={20} value={b[k]} onChange={(e) => setB({ [k]: Number(e.target.value), pattern: "custom" })} className="bg-white rounded-xl" data-testid={`step-${index}-${k}-input`} />
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <Label className="text-xs">Intro shown before starting</Label>
          <AiWriteButton field="intro" value={b.intro || ""} onChange={(v) => setB({ intro: v })} context={{ tutorial_title: tutorialTitle || "", step_title: step.title || "", step_type: "breathing" }} testId={`step-${index}-intro-ai`} />
        </div>
        <Textarea value={b.intro || ""} onChange={(e) => setB({ intro: e.target.value })} rows={2} className="bg-white rounded-xl" placeholder="A short, reassuring note about how to breathe." data-testid={`step-${index}-intro-input`} />
      </div>
    </div>
  );
};

export const StepEditor = ({ step, index, total, update, remove, move, tutorialTitle }) => {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5 sm:p-6 space-y-4" data-testid={`step-editor-${index}`}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center text-sm font-bold">{index + 1}</span>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          <button type="button" onClick={() => update({ type: "content" })} data-testid={`step-${index}-type-content`}
            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${step.type === "content" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"}`}><FileText className="w-3 h-3" /> Content</button>
          <button type="button" onClick={() => update({ type: "breathing", breathing: step.breathing || { pattern: "box", inhale: 4, hold_in: 4, exhale: 4, hold_out: 4, intro: "" } })} data-testid={`step-${index}-type-breathing`}
            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${step.type === "breathing" ? "bg-white shadow-sm text-indigo-700" : "text-slate-500 hover:text-slate-900"}`}><Wind className="w-3 h-3" /> Breathing</button>
        </div>
        <div className="ml-auto flex gap-1">
          <Button type="button" size="icon" variant="ghost" disabled={index === 0} onClick={() => move(-1)} aria-label="Move up" data-testid={`step-${index}-move-up`}><ChevronUp className="w-4 h-4" /></Button>
          <Button type="button" size="icon" variant="ghost" disabled={index === total - 1} onClick={() => move(1)} aria-label="Move down" data-testid={`step-${index}-move-down`}><ChevronDown className="w-4 h-4" /></Button>
          <Button type="button" size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={remove} aria-label="Remove step" data-testid={`step-${index}-remove`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Step title</Label>
        <Input value={step.title} onChange={(e) => update({ title: e.target.value })} className="rounded-xl" placeholder="e.g. Settling in" data-testid={`step-${index}-title-input`} />
      </div>
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <Label>Text <span className="text-xs text-slate-400 font-normal">(**bold**, *italic*, numbered or - bullet lists, blank line between paragraphs)</span></Label>
          <AiWriteButton field="step_body" value={step.body} onChange={(v) => update({ body: v })} context={{ tutorial_title: tutorialTitle || "", step_title: step.title || "", step_type: step.type }} testId={`step-${index}-ai`} />
        </div>
        <Textarea value={step.body} onChange={(e) => update({ body: e.target.value })} rows={5} className="rounded-xl" data-testid={`step-${index}-body-input`} />
      </div>

      {step.type === "breathing" ? <BreathingFields step={step} update={update} index={index} tutorialTitle={tutorialTitle} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Image</Label>
            <ImageUpload value={step.image_url} onChange={(v) => update({ image_url: v || null })} testId={`step-${index}-image`} height="h-36" />
          </div>
          <div className="space-y-2">
            <Label>Video (YouTube or Vimeo link)</Label>
            <Input value={step.video_url || ""} onChange={(e) => update({ video_url: e.target.value || null })} className="rounded-xl" placeholder="https://www.youtube.com/watch?v=…" data-testid={`step-${index}-video-input`} />
            {step.video_url && !videoEmbedUrl(step.video_url) && <p className="text-xs text-rose-600">Not a recognised YouTube or Vimeo link.</p>}
            {videoEmbedUrl(step.video_url) && <p className="text-xs text-emerald-700">Video link recognised.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
